<template>
  <a-drawer
    :open="store.drawerOpen"
    width="min(560px, 100vw)"
    title="只读诊断"
    @close="store.closeDetail"
  >
    <a-alert v-if="store.detailError" type="error" show-icon :message="store.detailError"
      ><template #action
        ><a-button size="small" @click="store.loadDetail">重试</a-button></template
      ></a-alert
    >
    <a-skeleton v-if="store.detailLoading && !store.detail" active :paragraph="{ rows: 9 }" />
    <template v-if="service">
      <div class="detail-heading">
        <h2>{{ service.name }}</h2>
        <a-tag :color="statusColors[service.status]">{{ statusLabels[service.status] }}</a-tag>
      </div>
      <p>{{ service.evidence.summary }}</p>
      <dl class="diagnostic-facts">
        <div>
          <dt>服务 ID</dt>
          <dd>
            <code>{{ service.id }}</code>
          </dd>
        </div>
        <div>
          <dt>分组</dt>
          <dd>{{ groupLabels[service.group] }}</dd>
        </div>
        <div>
          <dt>配置</dt>
          <dd>{{ service.configured ? '已配置' : '必要配置不完整' }}</dd>
        </div>
        <div>
          <dt>证据来源</dt>
          <dd>{{ sourceLabels[service.evidence.source] }}</dd>
        </div>
        <div>
          <dt>检查时间</dt>
          <dd>{{ formatTime(service.evidence.checkedAt) }}</dd>
        </div>
        <div>
          <dt>最近成功</dt>
          <dd>{{ formatTime(service.evidence.lastSuccessAt) }}</dd>
        </div>
        <div>
          <dt>最近失败</dt>
          <dd>{{ formatTime(service.evidence.lastFailureAt) }}</dd>
        </div>
        <div>
          <dt>耗时</dt>
          <dd>
            {{ service.evidence.latencyMs === null ? '—' : `${service.evidence.latencyMs} ms` }}
          </dd>
        </div>
        <div>
          <dt>错误分类</dt>
          <dd>
            {{
              service.evidence.errorCode ? (errorLabels[service.evidence.errorCode] ?? '未知') : '—'
            }}
          </dd>
        </div>
        <div v-for="(value, key) in service.metrics" :key="key">
          <dt>{{ metricLabels[key] ?? key }}</dt>
          <dd>{{ metricValue(String(key), value) }}</dd>
        </div>
      </dl>
      <a-space wrap
        ><a-button
          v-if="service.id.startsWith('queue:')"
          @click="emit('open-queue', service.id.slice(6))"
          >查看队列 Job</a-button
        ><a-button
          v-if="allowConfiguration && configurationAllowed"
          @click="
            router.push(service.configurationRoute!);
            store.closeDetail();
          "
          >打开配置</a-button
        ></a-space
      >
    </template>
    <template v-else-if="task">
      <div class="detail-heading">
        <h2>
          {{
            task.kind === 'agent'
              ? 'Agent 任务'
              : task.kind === 'creative'
                ? '创意任务'
                : '队列 Job'
          }}
        </h2>
        <a-tag>{{ taskStatusLabels[task.status] ?? '未知' }}</a-tag>
      </div>
      <dl class="diagnostic-facts">
        <div>
          <dt>完整 ID</dt>
          <dd>
            <code>{{ task.id }}</code>
          </dd>
        </div>
        <div>
          <dt>类型</dt>
          <dd>{{ kindLabels[task.category] ?? task.category }}</dd>
        </div>
        <div v-for="field in idFields" :key="field.key">
          <dt>{{ field.label }}</dt>
          <dd>
            <code>{{ task[field.key] ?? '—' }}</code>
          </dd>
        </div>
        <div>
          <dt>创建时间</dt>
          <dd>{{ formatTime(task.createdAt) }}</dd>
        </div>
        <div>
          <dt>最近更新</dt>
          <dd>{{ formatTime(task.updatedAt) }}</dd>
        </div>
        <div>
          <dt>当前阶段</dt>
          <dd>{{ taskStatusLabels[task.stage] ?? task.stage }}</dd>
        </div>
        <div>
          <dt>错误分类</dt>
          <dd>{{ task.errorCode ? (errorLabels[task.errorCode] ?? '未知') : '—' }}</dd>
        </div>
        <div v-for="(value, key) in task.metrics" :key="key">
          <dt>{{ metricLabels[key] ?? key }}</dt>
          <dd>{{ metricValue(String(key), value) }}</dd>
        </div>
      </dl>
    </template>
    <template #extra
      ><a-button :loading="store.detailLoading" @click="store.loadDetail" aria-label="刷新当前诊断"
        ><i class="ri-refresh-line" aria-hidden="true" /> 刷新</a-button
      ></template
    >
  </a-drawer>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import type { MonitorServiceView, MonitorTaskView } from 'shared';
import { useAdminRuntimeMonitorStore } from '@/stores/admin-runtime-monitor.store';
import { useAdminSessionStore } from '@/stores/admin-session.store';
import {
  errorLabels,
  formatTime,
  groupLabels,
  kindLabels,
  metricLabels,
  metricValue,
  statusColors,
  statusLabels,
  taskStatusLabels,
} from './monitor-format';
const store = useAdminRuntimeMonitorStore();
withDefaults(defineProps<{ allowConfiguration?: boolean }>(), { allowConfiguration: true });
const emit = defineEmits<{ 'open-queue': [id: string] }>();
const session = useAdminSessionStore();
const router = useRouter();
const service = computed(() =>
  store.detail && 'evidence' in store.detail ? (store.detail as MonitorServiceView) : undefined,
);
const task = computed(() =>
  store.detail && 'kind' in store.detail ? (store.detail as MonitorTaskView) : undefined,
);
const sourceLabels = {
  probe: '只读连接检查',
  runtime: '实际运行证据',
  configuration: '配置状态（尚无运行证据）',
};
const idFields = [
  { key: 'workspaceId', label: '工作台 ID' },
  { key: 'sessionId', label: '会话 ID' },
  { key: 'taskId', label: '关联任务 ID' },
] as const;
const configurationAllowed = computed(
  () =>
    !!service.value?.configurationRoute &&
    !!service.value.configurationPermission &&
    session.can(service.value.configurationPermission) &&
    session.can(
      service.value.configurationPermission === 'settings.manage'
        ? 'settings.read'
        : 'commerce.read',
    ),
);
</script>
<style scoped>
.detail-heading {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.detail-heading h2 {
  margin: 0;
  font-size: 18px;
}
.detail-heading :deep(.ant-tag-success) {
  color: var(--aw-success);
  background: var(--aw-surface);
}
.detail-heading :deep(.ant-tag-warning) {
  color: var(--aw-warning);
  background: var(--aw-surface);
}
.detail-heading :deep(.ant-tag-error) {
  color: var(--aw-danger);
  background: var(--aw-surface);
}
.diagnostic-facts {
  margin: 24px 0;
}
.diagnostic-facts > div {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid var(--aw-border);
}
.diagnostic-facts dt {
  color: var(--aw-text-secondary);
}
.diagnostic-facts dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.diagnostic-facts code {
  font-size: 12px;
  user-select: all;
}
@media (max-width: 560px) {
  .diagnostic-facts > div {
    grid-template-columns: 105px minmax(0, 1fr);
    gap: 12px;
  }
}
</style>
